/**
 * Journey field pack — extra properties for journey map tasks.
 *
 * Journey maps are `@type: process @view: journey` documents. The process
 * domain knows nothing about test status, personas, or stages — this field
 * pack declares those properties as first-class editable fields so the
 * property panel exposes them and the TestHealthProfile can color tasks by
 * test_status.
 *
 * See plans/software-design/user-journeys.md § 1 for the conceptual model and
 * plans/software-design/foundation-scaffold.md § Layer 2b for the full spec.
 */

import { FieldType, type FieldPack, type FieldManifest } from '../../../core/fields/types';
import type { DiffStatus, DiffRisk } from '../models/CodeEntity';

export type JourneyStage = 'Discovery' | 'Evaluate' | 'Activate' | 'First Value' | 'Habit' | 'Advocate' | 'Convert';

export type TestStatus = 'passing' | 'failing' | 'flaky' | 'not_covered';

/**
 * Additional properties that a journey task node carries. All are
 * optional because a task may be a pure behavioral/emotional step
 * with no test coverage (e.g., "sees HN post" has no metric_id).
 */
export interface JourneyTaskProps {
  stage?: JourneyStage;
  persona?: string; // persona_id from registry/use-cases.v1.json
  test_case_id?: string | string[];
  test_status?: TestStatus;
  failure_mode?: string;
  metric_id?: string;
  drilldown?: string; // path to a UX flow markdown file
  // Diff annotations are also valid on journey tasks (when a turn updates
  // the journey itself, e.g., adding a new stage).
  diff?: DiffStatus;
  risk?: DiffRisk;
}

export interface JourneyFieldDefinition {
  key: keyof JourneyTaskProps;
  label: string;
  kind: 'enum' | 'reference' | 'string' | 'text' | 'filepath';
  options?: readonly string[];
  references?: 'personas' | 'metrics' | 'test_cases';
  multiple?: boolean;
}

export const JOURNEY_STAGES: readonly JourneyStage[] = [
  'Discovery',
  'Evaluate',
  'Activate',
  'First Value',
  'Habit',
  'Advocate',
  'Convert',
];

export const TEST_STATUSES: readonly TestStatus[] = ['passing', 'failing', 'flaky', 'not_covered'];

export const JourneyFieldPack = {
  id: 'software-design.journey',
  label: 'Journey (QA + Product)',
  appliesTo: { diagramType: 'process' as const, view: 'journey' as const },
  fields: [
    { key: 'stage', label: 'Stage', kind: 'enum', options: JOURNEY_STAGES },
    { key: 'persona', label: 'Persona', kind: 'reference', references: 'personas' },
    { key: 'test_case_id', label: 'Test Case ID', kind: 'string', multiple: true },
    { key: 'test_status', label: 'Test Status', kind: 'enum', options: TEST_STATUSES },
    { key: 'failure_mode', label: 'Failure Mode', kind: 'text' },
    { key: 'metric_id', label: 'Metric ID', kind: 'reference', references: 'metrics' },
    { key: 'drilldown', label: 'Drill-down (UX flow)', kind: 'filepath' },
  ] satisfies readonly JourneyFieldDefinition[],
} as const;

export const JourneyFieldRegistryPack: FieldPack = {
  id: JourneyFieldPack.id,
  name: JourneyFieldPack.label,
  description: 'QA and product metadata fields for process journey map tasks.',
  version: '1.0.0',
  fields: JourneyFieldPack.fields.map((field, index): FieldManifest => ({
    id: field.key,
    label: field.label,
    type: toRegistryFieldType(field.kind),
    tags: ['journey', 'qa', 'product'],
    priority: 100 - index,
    multiline: field.kind === 'text',
    options: field.options?.map((option) => ({
      value: option,
      label: option,
    })),
    metadata: {
      appliesTo: JourneyFieldPack.appliesTo,
      journeyKind: field.kind,
      references: field.references,
      multiple: field.multiple ?? false,
    },
  })),
};

function toRegistryFieldType(kind: JourneyFieldDefinition['kind']): FieldType {
  if (kind === 'enum') return FieldType.SELECT;
  return FieldType.TEXT;
}
